'use client';

/**
 * Último recurso: se usa cuando falla el propio layout raíz, así que reemplaza
 * el documento entero y tiene que renderizar su propio <html>/<body>.
 */
import { useEffect } from 'react';
import { captureException } from '@/lib/observability';
import useClientStore from '@/store/useClientStore';
import { htmlLangOf, porIdiomaDelPais } from '@/lib/country';

/**
 * Este componente reemplaza el documento entero (incluido el provider de
 * idioma), así que el país sale del store del tenant, que es independiente de
 * los providers: si ya se había cargado un tenant de Brasil, la página de error
 * sale en portugués y con lang pt-BR; uno de EE.UU., en inglés y con lang en-US.
 * Sin tenant —o con cualquier otro país— sale exactamente como siempre.
 */
const TEXTOS = {
  es: {
    titulo: 'Algo salió mal',
    detalle: 'Ocurrió un error inesperado. Intenta recargar la página.',
    reintentar: 'Intentar de nuevo',
  },
  pt: {
    titulo: 'Algo deu errado',
    detalle: 'Ocorreu um erro inesperado. Tente recarregar a página.',
    reintentar: 'Tentar novamente',
  },
  en: {
    titulo: 'Something went wrong',
    detalle: 'An unexpected error occurred. Please try reloading the page.',
    reintentar: 'Try again',
  },
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const country = useClientStore((s) => s.client?.country);
  const tx = porIdiomaDelPais(country, TEXTOS);
  useEffect(() => {
    captureException(error, { source: 'nextjs-global-error', digest: error.digest });
  }, [error]);

  return (
    <html lang={htmlLangOf(country)}>
      <body
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '2rem',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>{tx.titulo}</h2>
        <p style={{ maxWidth: 360, fontSize: '0.875rem', opacity: 0.7 }}>
          {tx.detalle}
        </p>
        <button
          onClick={reset}
          style={{
            marginTop: 8,
            padding: '0.5rem 1.25rem',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '0.8125rem',
          }}
        >
          {tx.reintentar}
        </button>
      </body>
    </html>
  );
}
